/* eslint-disable no-unused-vars */
/* eslint-disable react/prop-types */
import React from "react";
import { useHistory } from "react-router-dom";
import CustomButton from "./CustomButton";

/**
 * ProHunterUpsellBanner - Premium promotional banner for Pro Hunter subscription
 * 
 * Usage: Place on Job Match Score Landing Page and Insights Page
 * Only shown to Freemium and Sprint Pack users
 * 
 * @param {Object} props
 * @param {string} props.placement - 'job_match' | 'insights' - Determines styling and messaging
 * @param {string} props.userTier - Current user tier: 'freemium' | 'sprint_pack' | 'pro_hunter'
 * @param {Function} props.onDismiss - Optional callback when banner is dismissed
 */
const ProHunterUpsellBanner = ({
  placement = "job_match",
  userTier = "freemium",
  onDismiss = null,
}) => {
  const history = useHistory();

  // Don't show for Pro Hunter users
  if (userTier === "pro_hunter") {
    return null;
  }

  const handleUpgrade = () => {
    history.push("/my-subscriptions");
  };

  const handleDismiss = (e) => {
    e.stopPropagation();
    if (onDismiss) {
      onDismiss();
    }
  };

  // Job Match placement styling
  if (placement === "job_match") {
    return (
      <div
        style={{
          background: "linear-gradient(135deg, #1E1B4B 0%, #3A6ED4 100%)",
          borderRadius: "12px",
          padding: "24px",
          marginBottom: "24px",
          position: "relative",
          boxShadow: "0 4px 12px rgba(58, 110, 212, 0.2)",
        }}
      >
        {/* Dismiss button */}
        {onDismiss && (
          <button
            onClick={handleDismiss}
            style={{
              position: "absolute",
              top: "12px",
              right: "12px",
              background: "transparent",
              border: "none",
              color: "#fff",
              cursor: "pointer",
              fontSize: "20px",
              lineHeight: 1,
              opacity: 0.7,
            }}
          >
            ×
          </button>
        )}

        {/* Icon */}
        <div
          style={{
            width: "48px",
            height: "48px",
            borderRadius: "10px",
            background: "rgba(255, 255, 255, 0.15)",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            marginBottom: "16px",
          }}
        >
          <svg width="24" height="24" fill="none" viewBox="0 0 24 24">
            <path
              d="M12 15l-2 5l-1.5-4.5L4 14l4.5-1.5L10 8l2 5l2-5l1.5 4.5L20 14l-4.5 1.5L14 20l-2-5z"
              stroke="#FFD700"
              strokeWidth="2"
              strokeLinecap="round"
              strokeLinejoin="round"
            />
          </svg>
        </div>

        {/* Content */}
        <h3
          style={{
            fontSize: "20px",
            fontWeight: "700",
            color: "#fff",
            margin: "0 0 8px 0",
          }}
        >
          Upgrade to Pro Hunter
        </h3>
        <p
          style={{
            fontSize: "14px",
            color: "rgba(255, 255, 255, 0.9)",
            margin: "0 0 16px 0",
            lineHeight: "1.5",
          }}
        >
          Unlock <strong>Unlimited Off-Platform Scans</strong> and{" "}
          <strong>Deep Peer-Match Analysis</strong>.
        </p>

        {/* Benefits */}
        <div
          style={{
            display: "flex",
            gap: "16px",
            marginBottom: "20px",
            flexWrap: "wrap",
          }}
        >
          {["Unlimited Off-Platform Matches", "Deep Peer Insight Analysis", "AI Resume Rewrites"].map(
            (benefit) => (
              <div
                key={benefit}
                style={{
                  display: "flex",
                  alignItems: "center",
                  gap: "6px",
                  fontSize: "12px",
                  color: "rgba(255, 255, 255, 0.85)",
                }}
              >
                <svg width="14" height="14" fill="none" viewBox="0 0 24 24">
                  <path
                    d="M20 6L9 17l-5-5"
                    stroke="#FFD700"
                    strokeWidth="2.5"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                  />
                </svg>
                <span>{benefit}</span>
              </div>
            )
          )}
        </div>

        {/* CTA Button */}
        <CustomButton
          additionalClass="p-3"
          text="View Pro Plans"
          backgroundColor="#FFD700"
          color="#1E1B4B"
          handleClick={handleUpgrade}
          style={{
            fontWeight: "700",
            fontSize: "14px",
            padding: "10px 24px",
            borderRadius: "8px",
          }}
        />
      </div>
    );
  }

  // Insights placement styling
  if (placement === "insights") {
    return (
      <div
        style={{
          background: "linear-gradient(135deg, #FFD700 0%, #7C3AED 50%, #3A6ED4 100%)",
          borderRadius: "12px",
          padding: "28px",
          marginBottom: "24px",
          position: "relative",
          boxShadow: "0 6px 16px rgba(124, 58, 237, 0.2)",
        }}
      >
        {/* Dismiss button */}
        {onDismiss && (
          <button
            onClick={handleDismiss}
            style={{
              position: "absolute",
              top: "12px",
              right: "12px",
              background: "transparent",
              border: "none",
              color: "#fff",
              cursor: "pointer",
              fontSize: "20px",
              lineHeight: 1,
              opacity: 0.7,
            }}
          >
            ×
          </button>
        )}

        {/* Premium shield icon */}
        <div
          style={{
            position: "absolute",
            top: "20px",
            right: "60px",
            opacity: 0.15,
          }}
        >
          <svg width="80" height="80" fill="none" viewBox="0 0 24 24">
            <path
              d="M12 2l3.09 2.26L22 6.27l-5 4.87 1.18 6.88L12 14l-6.18 4.02L7 11.14 2 6.27l6.91-2.01L12 2z"
              stroke="#fff"
              strokeWidth="1.5"
              strokeLinecap="round"
              strokeLinejoin="round"
            />
          </svg>
        </div>

        {/* Content */}
        <h3
          style={{
            fontSize: "24px",
            fontWeight: "700",
            color: "#fff",
            margin: "0 0 12px 0",
            textShadow: "0 2px 4px rgba(0, 0, 0, 0.1)",
          }}
        >
          SUPERCHARGE YOUR APPLICATIONS!
        </h3>
        <p
          style={{
            fontSize: "15px",
            color: "rgba(255, 255, 255, 0.95)",
            margin: "0 0 20px 0",
            lineHeight: "1.6",
          }}
        >
          Subscribe to <strong>Pro Hunter</strong> to make your next match and land your 10x engineer faster.
        </p>

        {/* Benefits */}
        <div
          style={{
            display: "flex",
            gap: "20px",
            marginBottom: "24px",
            flexWrap: "wrap",
          }}
        >
          {[
            "Unlimited Off-Platform Matches",
            "Deep Peer Insight Analysis",
            "AI Resume Rewrites",
          ].map((benefit) => (
            <div
              key={benefit}
              style={{
                display: "flex",
                alignItems: "center",
                gap: "8px",
                fontSize: "13px",
                color: "rgba(255, 255, 255, 0.9)",
                fontWeight: "500",
              }}
            >
              <svg width="16" height="16" fill="none" viewBox="0 0 24 24">
                <path
                  d="M20 6L9 17l-5-5"
                  stroke="#FFD700"
                  strokeWidth="2.5"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                />
              </svg>
              <span>{benefit}</span>
            </div>
          ))}
        </div>

        {/* CTA Button */}
        <CustomButton
          additionalClass="p-3"
          text="Subscribe and Upgrade to Pro"
          backgroundColor="#fff"
          color="#7C3AED"
          handleClick={handleUpgrade}
          style={{
            fontWeight: "700",
            fontSize: "15px",
            padding: "12px 32px",
            borderRadius: "8px",
            boxShadow: "0 4px 8px rgba(0, 0, 0, 0.15)",
          }}
        />
      </div>
    );
  }

  return null;
};

export default ProHunterUpsellBanner;
